import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { useTransition } from "@/context/TransitionContext";
import LanguageToggle from "@/components/LanguageToggle";
import { useFocusTrap } from "@/hooks/use-focus-trap";

const Menu = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { language, setLanguage, t } = useLanguage();
    // Removed useTransition hook usage as it's no longer needed for navigation
    const location = useLocation();
    const navigate = useNavigate();
    const menuRef = useRef<HTMLDivElement>(null);

    useFocusTrap(menuRef, isOpen, () => setIsOpen(false));

    // Close menu when route changes
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    // Prevent scrolling when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    // SEO: Localized menu items to improve internal linking relevance and UX for German users
    const menuItems = [
        { path: "/", label: t("menu.home") },
        { path: "/blog", label: t("menu.blog") },
        { path: "/perspectives", label: t("menu.startups") },
        { path: "/case-studies", label: t("menu.caseStudies") },
        { path: "/tools", label: t("menu.tools") },
        { path: "/about", label: t("menu.about") },
    ];

    const isActive = (path: string) => {
        if (path === "/") {
            return location.pathname === "/";
        }
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <>
            {/* Floating Menu Button */}
            <div className={`fixed top-6 right-4 md:right-8 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsOpen(true)}
                    className="no-particles bg-background/20 backdrop-blur-md border border-primary/20 hover:bg-background/40 w-12 h-12 rounded-full shadow-lg"
                    aria-label="Open menu"
                >
                    <MenuIcon className="h-6 w-6 text-foreground" />
                </Button>
            </div>

            {/* Backdrop */}
            <div
                className={`no-particles fixed inset-0 z-40 bg-background/80 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
            />

            {/* Side Drawer */}
            <aside
                ref={menuRef}
                className={`no-particles fixed top-0 right-0 z-50 h-full w-full sm:w-80 bg-background/95 backdrop-blur-xl border-l border-primary/20 shadow-2xl transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
                aria-modal="true"
                aria-label="Navigation Menu"
            >
                <div className="flex flex-col h-full p-6">
                    <div className="flex justify-between items-center mb-8">
                        {/* Social Links at Top */}
                        <div className="flex gap-3">
                            <a
                                href="https://github.com/kevske"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-all duration-300 hover:scale-110"
                                aria-label="GitHub"
                                title="GitHub"
                            >
                                <span className="sr-only">GitHub (opens in a new tab)</span>
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                </svg>
                            </a>
                            <a
                                href="https://www.linkedin.com/in/kevin-rassner/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-all duration-300 hover:scale-110"
                                aria-label="LinkedIn"
                                title="LinkedIn"
                            >
                                <span className="sr-only">LinkedIn (opens in a new tab)</span>
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                </svg>
                            </a>
                            <a
                                href="mailto:kevin.rassner@gmail.com"
                                className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-all duration-300 hover:scale-110"
                                aria-label="Email"
                                title="Email"
                            >
                                <span className="sr-only">Email</span>
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </a>
                            <Link
                                to="/impressum"
                                className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-all duration-300 hover:scale-110"
                                aria-label="Impressum"
                                title="Impressum"
                            >
                                <span className="sr-only">Impressum</span>
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </Link>
                        </div>

                        {/* Close Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setIsOpen(false)}
                            className="no-particles hover:bg-primary/10 rounded-full"
                            aria-label="Close menu"
                        >
                            <X className="h-6 w-6" />
                        </Button>
                    </div>

                    <nav className="flex flex-col space-y-6 flex-grow justify-center">
                        {menuItems.map((item) => {
                            const isItemActive = isActive(item.path);


                            // Regular Link for all other cases
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    title={item.label}
                                    aria-current={isItemActive ? "page" : undefined}
                                    className={`text-2xl font-bold transition-colors duration-200 hover:text-primary ${isItemActive
                                        ? "text-primary"
                                        : "text-muted-foreground"
                                        }`}
                                >
                                    {item.label}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="pt-6 border-t border-primary/20 flex justify-center">
                        <LanguageToggle currentLanguage={language} onLanguageChange={setLanguage} />
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Menu;
